import h from 'hyperscript';

import type { ConditionDto, DefaultConditionValueItemDto, SpatialConditionValueItemDto, WorkbookConditionValuesDto, WorkbookValueIntervalConditionValueItemDto, WorkbookQlConditionValueItemDto } from 'cadenza/workbook/model/condition-dto';
import { CONDITION_INTERVAL_VALUE_TYPE } from 'cadenza/workbook/conditions/condition-const';

import './create-condition.css';

export function conditionHasValues (condition: ConditionDto) {
  return condition.values && condition.values.valueItems.length > 0;
}

export function formatConditionValueAsText (values?: WorkbookConditionValuesDto): string {
  if (!values) {
    return '';
  }

  switch (values.conditionType) {
    case 'QlCondition':
      return formatQlConditionValueAsText(values);
    case 'PolygonCondition':
      return formatSpatialConditionValueAsText(values);
    default:
      return formatDefaultConditionValuesAsText(values);
  }
}

export function formatDefaultConditionValuesAsText (values: WorkbookConditionValuesDto) {
  return values.valueItems.map(value => {
    if (value.type === CONDITION_INTERVAL_VALUE_TYPE) {
      const conditionIntervalValueItem = value as WorkbookValueIntervalConditionValueItemDto;
      return `${conditionIntervalValueItem.startFormatted} - ${conditionIntervalValueItem.endFormatted}`;
    }
    const defaultConditionValueItem = value as DefaultConditionValueItemDto;
    return defaultConditionValueItem.text;
  }).join(', ');
}

/**
 * Returns a table, date, interval or other "default" condition's formatted values.
 *
 * @param condition
 * @return Formatted text representation of the condition's value, including formatting for excluded values.
 */
export function formatDefaultConditionValues (condition: ConditionDto) {
  if (!condition.values?.valueItems) {
    return null;
  }

  const excludeValues = condition.conditionConfiguration?.excludeValues ?? false;

  return condition.values.valueItems.map(value => {
    const text = value.type === CONDITION_INTERVAL_VALUE_TYPE
      ? formatIntervalValueItem(value as WorkbookValueIntervalConditionValueItemDto) // We know because of the value type field
      : formatSimpleValueItem(value as DefaultConditionValueItemDto);
    const span = h('span.d-read-only-condition-value-item', text);
    if (excludeValues) {
      span.classList.add('is-excluded');
    }
    return span;
  }).flatMap((span, index) => {
    return (index === condition.values!.valueItems.length - 1) // we know condition.values is not undefined because of check earlier
      ? [ span ]
      : [ span, ', ' ];
  });
}

function formatIntervalValueItem (value: WorkbookValueIntervalConditionValueItemDto) {
  return `${value.startFormatted} - ${value.endFormatted}`;
}

function formatSimpleValueItem (value: DefaultConditionValueItemDto) {
  if (value.type === 'tableCondition') {
    // when there is an attribute relation, we just want to show the first value
    return value.values[0];
  }
  return value.text;
}

export function formatQlConditionValueAsText (values: WorkbookConditionValuesDto) {
  return (values.valueItems[0] as WorkbookQlConditionValueItemDto).expressionString;
}

export function formatSpatialConditionValueAsText (values: WorkbookConditionValuesDto) {
  return (values.valueItems[0] as SpatialConditionValueItemDto)
    .values.map(it => it.printName).join(',');
}
